import {
  Alert,
  Box,
  Container,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { ReactNode } from 'react';

interface SectionHeaderProps {
  children: ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.bidlogiq.brick,
        color: theme.palette.common.white,
        py: 1,
        px: 2,
        mt: 3,
        mb: 2,
        mx: -4, // Extend to the edges of the Paper component's padding
      }}
    >
      <Typography variant="h5" component="h2" sx={{ textAlign: 'left' }}>
        {children}
      </Typography>
    </Box>
  );
};

const SharepointGuidePage = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        padding: '40px 20px',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ padding: 4, textAlign: 'center' }}>
          <img
            src="/Bidlogiq_Logo-Long_Full colours.png"
            alt="BIDLOGIQ Logo"
            style={{ maxWidth: '300px', marginBottom: '24px' }}
          />
          <Typography variant="h4" component="h1" gutterBottom>
            How to Get Your SharePoint API Credentials for BIDLOGIQ
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
            This guide will walk you through obtaining the five key pieces of information required
            for BIDLOGIQ to interact with your SharePoint document library. Using your own
            SharePoint ensures that your documents are securely stored in your own ecosystem, only
            visible to your team, and only temporarily accessed during the encrypted AI generation
            processes.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ textAlign: 'left' }}>
            Prerequisites:
          </Typography>
          <List sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText primary="You must have a Global Administrator or Application Administrator role in your Microsoft 365 tenant." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    You need access to the Azure Portal (
                    <Link href="https://portal.azure.com" target="_blank" rel="noopener">
                      portal.azure.com
                    </Link>
                    ).
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="You need a SharePoint site to store your documents (we'll show you how to create one if needed)." />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          <SectionHeader>Step 1: Create a SharePoint Site Dedicated to BIDLOGIQ</SectionHeader>
          <List dense sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText primary={<span>1. Go directly to your SharePoint home page:</span>} />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 2, display: 'block' }}>
                https://YOURCOMPANY.sharepoint.com
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText primary='2. Click "Create site"' />
            </ListItem>
            <ListItem>
              <ListItemText primary='3. Choose "Team site"' />
            </ListItem>
            <ListItem>
              <ListItemText primary='4. Choose "Standard Team"' />
            </ListItem>
            <ListItem>
              <ListItemText primary='5. Click "Use Template"' />
            </ListItem>
            <ListItem>
              <ListItemText primary="6. Fill in the site details:" />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Site name: "BIDLOGIQ SharePoint Integration, Click Next"' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Privacy: Select "Private, Click Create Site"' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={
                  <span>
                    – <b>Important:</b> Don't add any users! The BIDLOGIQ app will handle all user
                    interactions with file access to the SharePoint.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary='7. Click "Finish" after it is done setting up' />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    8. <b>Important:</b> Copy the full URL of your new site - you'll need this for
                    the steps below.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 2, display: 'block' }}>
                https://YOURCOMPANY.sharepoint.com/sites/BIDLOGIQSharePointIntegration
              </Box>
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              p: 2,
              color: theme.palette.bidlogiq.black,
              backgroundColor: theme.palette.bidlogiq.taupe,
              borderRadius: 1,
              border: '1px solid #000000',
            }}
          >
            <b>Alternative method:</b> You can also create a SharePoint site through the{' '}
            <Link href="https://admin.microsoft.com" target="_blank" rel="noopener">
              Microsoft 365 Admin Center
            </Link>{' '}
            by navigating to SharePoint &gt; Sites &gt; Active sites &gt; Create, but the direct
            method above is simpler.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <SectionHeader>Step 2: Find your Tenant ID</SectionHeader>
          <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
            This ID is the unique identifier for your entire organization’s Microsoft account.
          </Typography>
          <List dense sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    1. Navigate to the{' '}
                    <Link href="https://portal.azure.com" target="_blank" rel="noopener">
                      Azure Portal
                    </Link>{' '}
                    and sign in.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary='2. In the top search bar, search for and select "Microsoft Entra ID".' />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. On the “Overview” page, you will see the Tenant ID. Copy this value." />
            </ListItem>
          </List>

          <SectionHeader>
            Step 3: Create the BIDLOGIQ ‘app’ to get your Client ID & Client Secret
          </SectionHeader>
          <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
            You need to register BIDLOGIQ as an application in Azure so it has its own identity and
            credentials.
          </Typography>
          <List dense sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>Register a New App:</b> In Microsoft Entra ID, select Manage then "App
                    registrations" from the left menu, then click "+ New registration".
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary='– Name: Give your app a clear name (e.g., "BIDLOGIQ App").' />
            </ListItem>
            <ListItem>
              <ListItemText primary='– Supported account types: Leave the default option ("Accounts in this organizational directory only").' />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    – Redirect URI: Leave this <b>blank</b> (we don't need it for server-to-server
                    authentication).
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="– Click Register." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>Copy the Client ID:</b> On the “Overview” page of your new app, copy the
                    "Application (client) ID". This is your Client ID.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>Create a Client Secret:</b>
                  </span>
                }
              />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– In the app’s menu, go to Manage then "Certificates & secrets".' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Click "+ New client secret".' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Add a description (e.g., "BIDLOGIQ Secret"), select an expiration period, and click Add.' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={
                  <span>
                    – <b>IMPORTANT:</b> A secret will be generated. Immediately copy the{' '}
                    <b>Value</b> and save it in a secure location. This is your Client Secret, and
                    it will be hidden forever after you navigate away from this page.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>Grant API Permissions:</b>
                  </span>
                }
              />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Go to "API permissions" in the app’s menu.' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Click "+ Add a permission", then select "Microsoft Graph".' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– Choose "Application permissions".' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText primary='– In the search box, type "Sites.Selected", click to expand the "Sites" row, check the box next to it, and click "Add permissions".' />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={
                  <span>
                    - <b>IMPORTANT:</b> Finally, click the "Grant admin consent" button at the top
                    of the list and click confirm.
                  </span>
                }
              />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <Alert
                severity="success"
                sx={{
                  width: '100%',
                  fontSize: '1rem',
                  mt: 1,
                  backgroundColor: theme.palette.bidlogiq.rust,
                  border: '1px solid #000000',
                  color: theme.palette.common.white,
                  opacity: 0.7,
                  '& .MuiAlert-icon': {
                    color: theme.palette.common.white,
                  },
                }}
              >
                <b>Verification:</b> After granting consent, ensure you see green checkmarks under
                the "Status" column for the <b>Sites.Selected</b> permission.
              </Alert>
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              p: 2,
              color: theme.palette.bidlogiq.black,
              backgroundColor: theme.palette.bidlogiq.taupe,
              borderRadius: 1,
              border: '1px solid #000000',
            }}
          >
            <b>Note on Sites.Selected:</b> This permission allows an app to access only the
            SharePoint site collections it has been explicitly granted permission to. The
            application will be blocked with a "forbidden" error if it attempts to access any other
            site.
          </Typography>

          <SectionHeader>Step 4: Grant BIDLOGIQ Access to Your SharePoint Site</SectionHeader>
          <Typography variant="h6" paragraph sx={{ textAlign: 'left' }}>
            <b>CRITICAL STEP:</b> You must grant your BIDLOGIQ app permission to access your
            SharePoint site.
          </Typography>
          <List dense sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>1. Go to the App Permissions Page:</b> In your browser, navigate to:
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 2, display: 'block' }}>
                https://YOURCOMPANY.sharepoint.com/sites/YourSharePointSite/_layouts/15/appinv.aspx
              </Box>
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={
                  <span>
                    <b>Examples of the complete URL:</b>
                  </span>
                }
              />
            </ListItem>
            <ListItem sx={{ pl: 6 }}>
              <ListItemText primary="• If your site is:" />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 8, display: 'block' }}>
                https://YOURCOMPANY.sharepoint.com/sites/BIDLOGIQSharePointIntegration
              </Box>
            </ListItem>
            <ListItem sx={{ pl: 6 }}>
              <ListItemText primary="Go to:" />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 8, display: 'block' }}>
                https://YOURCOMPANY.sharepoint.com/sites/BIDLOGIQSharePointIntegration/_layouts/15/appinv.aspx
              </Box>
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={
                  <span>
                    <b>Important:</b> Use the exact same URL for the SharePoint you just created,
                    just add:
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 6, display: 'block' }}>
                /_layouts/15/appinv.aspx
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>2. Enter Your Client ID:</b> In the "App Id" field, paste your Client ID from
                    Step 3 and click "Lookup".
                  </span>
                }
              />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={
                  <span>
                    <b>If prompted for "App Domain":</b> Enter your SharePoint domain, e.g.:
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 6, display: 'block' }}>
                YOURCOMPANY.sharepoint.com
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>3. Grant Write Permission:</b> In the large text box labeled "Permission
                    Request XML", copy and paste this exact text:
                  </span>
                }
              />
            </ListItem>
            <ListItem sx={{ pl: 4 }}>
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f5f5f5',
                  color: theme.palette.bidlogiq.black,
                  padding: 1,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  overflow: 'auto',
                  border: '1px solid #ddd',
                }}
              >
                {`<AppPermissionRequests AllowAppOnlyPolicy="true">
  <AppPermissionRequest Scope="http://sharepoint/content/sitecollection/web" Right="Write" />
</AppPermissionRequests>`}
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <b>4. Complete the Process:</b> Click "Create" and then click "Trust It" to
                    grant the permissions.
                  </span>
                }
              />
            </ListItem>
          </List>

          <SectionHeader>Step 5: Get the SharePoint Site ID</SectionHeader>
          <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
            This is the unique ID for the specific SharePoint site you want to access.
          </Typography>
          <List dense sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    1. Go to the{' '}
                    <Link
                      href="https://developer.microsoft.com/en-us/graph/graph-explorer"
                      target="_blank"
                      rel="noopener"
                    >
                      Microsoft Graph Explorer
                    </Link>{' '}
                    and sign in.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Verify you are logged in by clicking on the Profile icon in the top right corner and confirming your account details." />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Ensure the request method is set to GET." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    4. In the URL field, enter the following address, replacing the hostname and
                    site-path with your site's details:
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 2, display: 'block' }}>
                https://graph.microsoft.com/v1.0/sites/&#123;hostname&#125;:/sites/&#123;site-path&#125;
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText primary="– Example: For a SharePoint site at:" />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 4, display: 'block' }}>
                https://YOURCOMPANY.sharepoint.com/sites/BIDLOGIQSharePointIntegration
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText primary="The URL would be:" />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 4, display: 'block' }}>
                https://graph.microsoft.com/v1.0/sites/YOURCOMPANY.sharepoint.com:/sites/BIDLOGIQSharePointIntegration
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText primary='5. Click "Run query".' />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    6. In the response pane below, find and copy the <code>id</code> value. This is
                    your Site ID.
                  </span>
                }
              />
            </ListItem>
          </List>

          <SectionHeader>Step 6: Get the Document Library (Drive) ID</SectionHeader>
          <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
            This is the ID for the specific folder (like “Documents”) within the site.
          </Typography>
          <List dense sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    1. Using the Site ID from the previous step, construct the following URL in
                    Graph Explorer:
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 2, display: 'block' }}>
                https://graph.microsoft.com/v1.0/sites/&#123;Your-Site-ID&#125;/drives
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    – Example: Replace the entire Your-Site-ID part with your Site ID, your URL
                    would look like this:
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <Box component="code" sx={{ fontSize: '0.9rem', pl: 4, display: 'block' }}>
                https://graph.microsoft.com/v1.0/sites/YOURCOMPANY.sharepoint.com,THIS-IS-A-FAKE-SITEID-xxxxxxx,xxxxxxxx-xxxxx-xxxxx-123x4-567890abcdef/drives
              </Box>
            </ListItem>
            <ListItem>
              <ListItemText primary='2. Click "Run query".' />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    3. The response will list all document libraries on the site. Find “Documents”
                    and copy its corresponding <code>id</code> value. This is your Drive ID.
                  </span>
                }
              />
            </ListItem>
          </List>

          <SectionHeader>Step 7. You're ready to create your account!</SectionHeader>
          <ListItem>
            <ListItemText
              primary={
                <span>
                  Return to the onboarding page, complete the form, and click "Create Account".
                </span>
              }
            />
          </ListItem>
          <SectionHeader>Summary of Your Credentials</SectionHeader>
          <List sx={{ textAlign: 'left' }}>
            <ListItem>
              <ListItemText primary="✓ Tenant ID" secondary="The ID you copied in Step 2" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="✓ Client ID"
                secondary="The Application (client) ID you copied in Step 3"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="✓ Client Secret"
                secondary="The secret value you saved in Step 3"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="✓ API Permissions"
                secondary="Sites.Selected (Application) granted in Step 3"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="✓ Site Permissions"
                secondary="Write access granted in Step 4"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="✓ Site ID" secondary="The site's unique ID from Step 5" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="✓ Drive ID"
                secondary="The document library's unique ID from Step 6"
              />
            </ListItem>
          </List>
        </Paper>
      </Container>
    </Box>
  );
};

export default SharepointGuidePage;
